"use client";

import { useMemo, useCallback } from "react";
import { SUPPORTED_EXCHANGES } from "@cede/types";
import { WALLET_STATUS } from "@/constants/wallet";
import { useAuthenticationProvider } from "@/hooks/authentication/useAuthenticationProvider";
import { useApi } from "@/hooks/core/useApi";
import { useAuthenticatedWalletConnection } from "@/hooks/core/useAuthenticatedWalletConnection";
import { useSignTypedDataWithConnection } from "@/hooks/core/useSignTypedDataWithConnection";
import { isWalletActionError, useWalletActionGuard } from "@/hooks/core/useWalletActionGuard";
import { useHubUser } from "@/hooks/user/useHubUser";
import {
  prepareGrvtBuilder,
  authorizeGrvtBuilder,
  formatForWagmiSignTypedData,
  splitSignature,
} from "@/utils/airdrop-farming/grvt-builder-auth";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useAccount } from "wagmi";

/**
 * Session status for GRVT connection flow.
 * Compatible with ConnectAccountStep expectations.
 */
export type GrvtSessionStatus = "checking-wallet" | "needs-wallet" | "needs-approval" | "checking-activation" | "ready";

/**
 * Hook for managing GRVT connection flow via builder authorization.
 *
 * Flow:
 * 1. User connects their EVM wallet (wagmi)
 * 2. User enters their GRVT funding address (pre-filled from wallet, editable)
 * 3. Backend generates keypair + EIP-712 typed data (prepare phase)
 * 4. User signs the EIP-712 message with their wallet (wagmi signTypedData)
 * 5. Signature sent to backend which authorizes with GRVT and stores credentials
 */
export function useGrvtConnection() {
  const { auth, hubUser, walletStatus } = useHubUser();
  const apiHelper = useApi();
  const { address: wagmiAddress } = useAccount();
  const { authenticated } = useAuthenticationProvider();
  const queryClient = useQueryClient();
  const { authenticatedWalletAddress } = useAuthenticatedWalletConnection();
  const { prepareMainEvmAction } = useWalletActionGuard();
  const { signTypedDataAsync } = useSignTypedDataWithConnection();
  const requiredWalletAddress = authenticatedWalletAddress ?? wagmiAddress ?? hubUser?.defiAddress ?? undefined;
  const connectedAddress = walletStatus === WALLET_STATUS.CONNECTED ? requiredWalletAddress : undefined;
  const isWalletConnected = walletStatus === WALLET_STATUS.CONNECTED && Boolean(requiredWalletAddress);

  const grvtAccounts = useMemo(
    () => hubUser?.exchangeAccounts?.filter((a) => a.exchangeId === SUPPORTED_EXCHANGES.GRVT) ?? [],
    [hubUser?.exchangeAccounts],
  );

  const hasGrvtAccount = grvtAccounts.length > 0;

  const {
    mutateAsync: connectGrvtBuilder,
    isPending: isConnecting,
    error: connectionError,
    reset: resetConnectionError,
  } = useMutation({
    mutationFn: async (params: { userFundingAddress: string }) => {
      await prepareMainEvmAction();

      // Phase 1: Prepare — get EIP-712 typed data from backend
      const prepareResult = await prepareGrvtBuilder(apiHelper, auth, params.userFundingAddress);

      // Phase 2: Sign — user signs EIP-712 message with wallet
      const wagmiData = formatForWagmiSignTypedData(prepareResult.eip712TypedData);
      const signature = await signTypedDataAsync({
        domain: wagmiData.domain,
        types: wagmiData.types,
        primaryType: wagmiData.primaryType,
        message: wagmiData.message,
      });

      const { r, s, v } = splitSignature(signature);

      // Phase 3: Authorize — send signature to backend
      await authorizeGrvtBuilder(apiHelper, auth, {
        prepareId: prepareResult.prepareId,
        signatureR: r,
        signatureS: s,
        signatureV: v,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
      enqueueSnackbar("GRVT account connected successfully", { variant: "success" });
    },
    onError: (error) => {
      if (isWalletActionError(error)) return;
      const msg = error instanceof Error ? error.message : "Failed to connect GRVT";
      enqueueSnackbar(msg, { variant: "error" });
    },
  });

  const approveAgent = useCallback(
    (params?: { userFundingAddress?: string }) => {
      const fundingAddress = params?.userFundingAddress ?? requiredWalletAddress ?? "";
      return connectGrvtBuilder({ userFundingAddress: fundingAddress });
    },
    [connectGrvtBuilder, requiredWalletAddress],
  );

  const sessionStatus: GrvtSessionStatus =
    walletStatus === WALLET_STATUS.INITIALIZING || walletStatus === WALLET_STATUS.CONNECTING
      ? "checking-wallet"
      : !authenticated || !requiredWalletAddress
        ? "needs-wallet"
        : isConnecting
          ? "checking-activation"
          : hasGrvtAccount
            ? "ready"
            : "needs-approval";

  return {
    // Wallet state
    connectedAddress,
    isWalletConnected,

    // Account state
    grvtAccounts,
    hasGrvtAccount,

    // Actions
    connectGrvtBuilder,
    isConnecting,
    connectionError,
    resetConnectionError,

    // Session status (compatible with ConnectAccountStep)
    sessionStatus,

    // ── Shape compatible with useHyperliquidConnection for useLaunchBotForm ──
    agentData: {
      accountId: grvtAccounts[0]?.id ?? 0,
      agentAddress: grvtAccounts[0]?.publicApiKey ?? "",
      agentName: "",
    },
    hasAgentData: hasGrvtAccount,
    isLoadingAgent: false,
    isCheckingEligibility: false,
    isNotEligibleError: false,
    eligibilityData: { eligible: true } as const,
    isEligible: true,
    // Map GRVT connect → HL approve shape
    approveAgent,
    isApproving: isConnecting,
    approvalError: connectionError,
    resetApprovalError: resetConnectionError,
    // Not applicable for GRVT
    updateHyperliquidAgentStatus: async () => {},
    isUpdatingAgentStatus: false,
    isLoading: isConnecting,
    walletStatus,

    // Authenticated check
    authenticated,
  };
}
